/* Группы клиентов (GRP-1, GRP-5): список групп; клик по строке открывает то же окно,
   что и создание, — название, описание, участники, — плюс удаление. Клиент состоит
   не больше чем в одной группе: занятые другими группами видны, но не выбираются. */
import { trpc } from '@tg/app-core';
import { norm } from '@tg/domain';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clientProgSub, useClients, useGroups, type Group } from '../lib/data';
import { grpIni, ini, plural } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { byNum, byStr, DataTable, type Col } from '../ui/DataTable';
import { ICON } from '../ui/icons';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';

export function Groups() {
  const gs = useGroups().data;
  const cs = useClients().data;
  usePageTitle('Группы', gs?.length ?? null);
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState<Group | null | 'new'>(null);
  const openId = sp.get('id');
  const opened = edit ?? (openId && gs?.find((g) => g.id === openId)) ?? null;
  const name = (cid: string) => cs?.find((c) => c.id === cid)?.name || '';
  const photo = (cid: string) => cs?.find((c) => c.id === cid)?.photo;
  const all = gs || [];
  const list = all.filter((G) => !q || norm(G.name).includes(norm(q)) || G.members.some((m) => norm(name(m.clientId)).includes(norm(q))));
  const cols: Col<Group>[] = [
    {
      k: 'n',
      n: 'Группа',
      sort: byStr((g) => g.name),
      cell: (G) => (
        <div className="cellname">
          <span className="cav grp">{grpIni(G.name)}</span>
          <div style={{ minWidth: 0 }}>
            <div className="n">{G.name}</div>
            <div className="s">{G.about || 'без описания'}</div>
          </div>
        </div>
      ),
    },
    {
      k: 'm',
      n: 'Участники',
      w: '260px',
      sort: byNum((g) => g.members.length),
      cell: (G) => (
        <div className="gstack">
          <span className="stack">
            {G.members.slice(0, 5).map((m) => (
              <span className="av s" key={m.clientId} title={name(m.clientId)}>
                {photo(m.clientId) ? <img src={photo(m.clientId)!} alt="" /> : ini(name(m.clientId))}
              </span>
            ))}
          </span>
          <s>{G.members.length ? G.members.length + ' ' + plural(G.members.length, 'участник', 'участника', 'участников') : 'никого'}</s>
        </div>
      ),
    },
    {
      k: 'a',
      n: '',
      w: '150px',
      r: true,
      cell: (G) => (
        <Link className="btn gh sm" to={'/calendar?group=' + G.id}>
          <ICON.cal /> Календарь
        </Link>
      ),
    },
  ];
  const close = () => {
    setEdit(null);
    if (openId) setSp({}, { replace: true });
  };
  return (
    <div className="cons one">
      <main>
        <section className="pane" id="page">
          <div className="pane-h">
            <label className="search" style={{ width: 260 }}>
              <ICON.search />
              <input id="q" placeholder="Группа или участник…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <span className="sp"></span>
            <button className="btn" id="gNew" onClick={() => setEdit('new')}>
              <ICON.plus /> Создать группу
            </button>
          </div>
          {!gs ? null : list.length ? (
            <DataTable id="grp" cols={cols} rows={list} onRow={(G, e) => !(e.target as Element).closest("a,button") && setEdit(G)} fixed />
          ) : all.length ? (
            <div className="empty">
              <div className="t">Ничего не нашли</div>
              <div className="d">Измените поиск</div>
            </div>
          ) : (
            <div className="empty">
              <div className="t">Групп пока нет</div>
              <div className="d">Соберите в группу тех, кто занимается по одной программе, — тренировку напишете один раз, и она встанет в календарь каждому.</div>
            </div>
          )}
        </section>
      </main>
      {opened && <GroupModal G={opened === 'new' ? null : opened} all={all} onClose={close} />}
    </div>
  );
}

/* ─── Одно окно на создание, правку и удаление ─── */
function GroupModal({ G, all, onClose }: { G: Group | null; all: Group[]; onClose: () => void }) {
  /* Замороженные клиенты в группу не добавляются */
  const cs = (useClients().data || []).filter((c) => !c.frozenAt);
  const utils = trpc.useUtils();
  const was = new Set(G ? G.members.map((m) => m.clientId) : []);
  const [n, setN] = useState(G?.name || '');
  const [about, setAbout] = useState(G?.about || '');
  const [pick, setPick] = useState(() => new Set(was));
  const [q, setQ] = useState('');
  const [confirm, setConfirm] = useState(false);
  const refresh = () => {
    utils.trainer.groups.invalidate();
    utils.trainer.clients.invalidate();
    utils.trainer.groupsTill.invalidate();
  };
  const save = trpc.trainer.saveGroup.useMutation({
    onSuccess: (g) => {
      refresh();
      onClose();
      toast(G ? `Группа «${g.name}» сохранена` : `Группа «${g.name}» создана · ${pick.size} ${plural(pick.size, 'участник', 'участника', 'участников')}`);
    },
    onError: (e) => toast(e.message || 'Не сохранилось'),
  });
  const del = trpc.trainer.deleteGroup.useMutation({
    onSuccess: () => {
      refresh();
      onClose();
      toast(`Группа «${G!.name}» удалена`);
    },
    onError: (e) => toast(e.message || 'Не удалилось'),
  });
  const other = (cid: string) => all.find((g) => g.id !== G?.id && g.members.some((m) => m.clientId === cid)) || null;
  const rows = cs
    .filter((c) => !q || norm(c.name).includes(norm(q)))
    .sort((a, b) => +pick.has(b.id) - +pick.has(a.id) || +!!other(a.id) - +!!other(b.id) || a.name.localeCompare(b.name, 'ru'));
  const add = [...pick].filter((id) => !was.has(id)).length,
    rm = [...was].filter((id) => !pick.has(id)).length;
  const sum = !G
    ? pick.size
      ? 'Тренировки группы встанут каждому с сегодняшнего дня'
      : 'Участников можно добавить и потом'
    : add || rm
      ? [add ? 'добавятся ' + add + ' — тренировки группы встанут им с сегодняшнего дня' : '', rm ? 'уйдут ' + rm + ' — будущие тренировки группы у них уберутся, изменённые под них останутся' : '']
          .filter(Boolean)
          .join(' · ')
          .replace(/^./, (s) => s.toUpperCase())
      : '';
  const doSave = () => {
    if (!n.trim()) return toast('Назовите группу');
    save.mutate({ id: G?.id, name: n.trim(), about: about.trim() || null, members: [...pick] });
  };
  const foot = confirm ? (
    <>
      <span className="gsum">Удалить группу «{G!.name}»? Тренировки, которые уже стоят у участников, останутся у них.</span>
      <span className="sp"></span>
      <button className="btn gh" id="gDelNo" onClick={() => setConfirm(false)}>
        Не удалять
      </button>
      <button className="btn rm" id="gDelOk" disabled={del.isPending} onClick={() => del.mutate({ id: G!.id })}>
        Удалить
      </button>
    </>
  ) : (
    <>
      {G && (
        <button className="btn gh" id="gDel" style={{ color: 'var(--rm)' }} onClick={() => setConfirm(true)}>
          <ICON.x /> Удалить группу
        </button>
      )}
      <span className="sp"></span>
      <button className="btn gh" onClick={onClose}>
        Отмена
      </button>
      <button className="btn" id="gSave" disabled={save.isPending} onClick={doSave}>
        {G ? 'Сохранить' : 'Создать группу'}
      </button>
    </>
  );
  return (
    <Modal title={G ? 'Группа' : 'Новая группа'} wide onClose={onClose} foot={foot}>
      <div className="formfld">
        <span className="k">Название</span>
        <input className="inp" id="gN" autoFocus value={n} onChange={(e) => setN(e.target.value)} placeholder="Например, ЛФК · утро" autoComplete="off" />
      </div>
      <div className="formfld" style={{ marginTop: 12 }}>
        <span className="k">Описание · необязательно</span>
        <input className="inp" id="gA" value={about} onChange={(e) => setAbout(e.target.value)} placeholder="Кому и зачем эта группа" />
      </div>
      <div className="formfld" style={{ marginTop: 16 }}>
        <span className="k">
          Участники · <span id="gCnt">{pick.size}</span>
        </span>
        <label className="search gsearch">
          <ICON.search />
          <input id="gQ" placeholder="Найти клиента" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="gpick" id="gPick">
          {rows.length ? (
            rows.map((c) => {
              const o = other(c.id);
              if (o)
                return (
                  <div className="cl dis" key={c.id}>
                    <span className="box"></span>
                    <span className="av">{c.photo ? <img src={c.photo} alt="" /> : ini(c.name)}</span>
                    <span className="who">
                      <b>{c.name}</b>
                      <s>в группе «{o.name}» — сначала уберите оттуда</s>
                    </span>
                  </div>
                );
              return (
                <button
                  key={c.id}
                  className={'cl ' + (pick.has(c.id) ? 'on' : '')}
                  onClick={() => {
                    const p = new Set(pick);
                    p.has(c.id) ? p.delete(c.id) : p.add(c.id);
                    setPick(p);
                  }}
                >
                  <span className="box">
                    <ICON.chk />
                  </span>
                  <span className="av">{c.photo ? <img src={c.photo} alt="" /> : ini(c.name)}</span>
                  <span className="who">
                    <b>{c.name}</b>
                    <s>{clientProgSub(c)}</s>
                  </span>
                </button>
              );
            })
          ) : (
            <div className="hint">Никого не нашли</div>
          )}
        </div>
        <div className="gsum" id="gSum">
          {sum}
        </div>
      </div>
    </Modal>
  );
}
